import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, LogIn, LogOut, Menu, Search, ShoppingBag, User, UserPlus, X } from "lucide-react";
import { SearchBar } from "./SearchBar";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";

// Mega-menu structure — each top nav item can expose grouped sub-categories.
// Groups render side-by-side on hover.
const primaryLinks = [
  {
    to: "/moissanite-watches",
    label: "Watches",
    groups: [
      { title: "Shop By Category", items: [
        { to: "/mens-watch", label: "Men's Watch" },
        { to: "/womens-watch", label: "Women's Watch" },
      ]},
      { title: "Shop By Style", items: [
        { to: "/iced-out-watch", label: "Iced Out Watch" },
        { to: "/plain-watch", label: "Plain Watch" },
      ]},
      { title: "Shop By Strap", items: [
        { to: "/bust-down-watch", label: "Bust Down Watches" },
        { to: "/rubber-belt-watch", label: "Rubber Strap Watch" },
        { to: "/leather-belt-watch", label: "Leather Strap Watch" },
      ]},
    ],
  },
  {
    to: "/cuban-chains",
    label: "Chains",
    groups: [
      { title: "Shop By Category", items: [
        { to: "/mens-chain", label: "Men's Chain" },
        { to: "/womens-chain", label: "Women's Chain" },
      ]},
      { title: "Shop By Style", items: [
        { to: "/cuban-link-chain", label: "Cuban Link Chain" },
        { to: "/gold-chains", label: "Gold Chains" },
      ]},
    ],
  },
  { to: "/grillz", label: "Grillz" },
  {
    to: "/iced-bracelets",
    label: "Bracelets",
    groups: [
      { title: "Shop By Style", items: [
        { to: "/cuban-bracelet", label: "Cuban Bracelet" },
      ]},
      { title: "Shop By Category", items: [
        { to: "/tennis-bracelet", label: "Tennis Bracelet" },
      ]},
    ],
  },
  {
    to: "/pendants",
    label: "Pendants",
    groups: [
      { title: "Shop By Category", items: [
        { to: "/mens-pendant", label: "Men's Pendant" },
        { to: "/womens-pendant", label: "Women's Pendant" },
      ]},
      { title: "Shop By Style", items: [
        { to: "/hip-hop-pendant", label: "Hip Hop Pendant" },
        { to: "/enamel-pendant", label: "Enamel Pendants" },
        { to: "/delicate-pendant", label: "Delicate Pendant" },
      ]},
    ],
  },
  {
    to: "/rings",
    label: "Rings",
    groups: [
      { title: "Shop By Category", items: [
        { to: "/mens-ring", label: "Men's Ring" },
        { to: "/womens-ring", label: "Women's Ring" },
      ]},
      { title: "Shop By Style", items: [
        { to: "/hip-hop-ring", label: "Hip-hop Ring" },
        { to: "/delicate-ring", label: "Delicate Ring" },
      ]},
    ],
  },
  { to: "/moissanite-glasses-sunglasses", label: "Iced Out Glasses", mobileOnly: true },
  { to: "/blog", label: "Blog" },
];

const utilityLinks = [
  { to: "/wishlist", label: "Wishlist" },
  { to: "/sitemap", label: "Sitemap" },
  { to: "/cart", label: "Cart" },
];

export const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { count: wCount } = useWishlist();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const signedIn = user && typeof user === "object";

  return (
    <>
      <header className="sticky top-0 z-50 border-b-2 border-black bg-white/90 backdrop-blur-xl" data-testid="main-navbar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-hamburger-btn"
            className="flex h-10 w-10 items-center justify-center border-2 border-black bg-white text-black transition-colors duration-200 hover:bg-black hover:text-white md:hidden"
          >
            <Menu size={18} />
          </button>

          <Link to="/" data-testid="nav-logo" className="flex items-center font-display text-lg font-black tracking-tighter text-black">
            VVS<span className="ml-1 bg-black px-1.5 py-0.5 text-white">LAB</span>
            <span className="ml-2 hidden text-[10px] font-normal uppercase tracking-[0.3em] text-zinc-500 sm:inline">jewelry</span>
          </Link>

          <nav className="hidden items-center gap-7 md:flex" data-testid="desktop-nav">
            {primaryLinks.filter((l) => !l.mobileOnly).map((l) => (
              <div key={l.to} className="group relative">
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-200 ${isActive ? "text-black underline underline-offset-8 decoration-2" : "text-zinc-500 hover:text-black"}`
                  }
                >
                  {l.label}
                </NavLink>
                {l.groups && (
                  <div
                    data-testid={`mega-menu-${l.label.toLowerCase()}`}
                    className="pointer-events-none invisible absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100"
                  >
                    <div className="border-2 border-black bg-white p-6 shadow-2xl" style={{ minWidth: `${Math.max(280, l.groups.length * 200)}px` }}>
                      <div className={`grid gap-8`} style={{ gridTemplateColumns: `repeat(${l.groups.length}, minmax(160px, 1fr))` }}>
                        {l.groups.map((g) => (
                          <div key={g.title}>
                            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">{g.title}</p>
                            <ul className="space-y-2">
                              {g.items.map((it) => (
                                <li key={it.to}>
                                  <Link
                                    to={it.to}
                                    data-testid={`mega-menu-link-${it.to.slice(1)}`}
                                    className="block whitespace-nowrap text-sm font-medium text-black hover:underline"
                                  >
                                    {it.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 border-t border-zinc-200 pt-3">
                        <Link to={l.to} className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 hover:text-black">
                          View all {l.label.toLowerCase()} →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search products"
              data-testid="nav-search-btn"
              className="relative hidden h-10 w-10 items-center justify-center border-2 border-black bg-white text-black transition-colors duration-200 hover:bg-black hover:text-white md:flex"
            >
              <Search size={15} />
            </button>
            <Link
              to="/wishlist"
              data-testid="nav-wishlist-link"
              aria-label="Wishlist"
              className="relative hidden h-10 w-10 items-center justify-center border-2 border-black bg-white text-black transition-colors duration-200 hover:bg-black hover:text-white sm:flex"
            >
              <Heart size={15} />
              {wCount > 0 && (
                <span data-testid="wishlist-count-badge" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#1e40af] text-[10px] font-bold text-white">
                  {wCount}
                </span>
              )}
            </Link>
            {signedIn ? (
              <Link
                to="/wishlist"
                data-testid="nav-account-link"
                className="hidden items-center gap-2 border-2 border-black bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:bg-black hover:text-white lg:flex"
              >
                <User size={13} />
                <span className="max-w-[80px] truncate">{user.name || "Account"}</span>
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="hidden items-center gap-2 border-2 border-black bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:bg-black hover:text-white lg:flex"
              >
                <LogIn size={13} /> Sign In
              </Link>
            )}

            <Link to="/cart" data-testid="nav-cart-link" aria-label={count > 0 ? `Shopping cart, ${count} item${count === 1 ? "" : "s"}` : "Shopping cart"} className="relative flex items-center gap-2 border-2 border-black bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:bg-black hover:text-white sm:px-4">
              <ShoppingBag size={15} aria-hidden="true" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-black text-[10px] font-bold text-white">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {searchOpen && <SearchBar variant="overlay" onClose={() => setSearchOpen(false)} />}

      {/* Mobile sheet */}
      <div
        className={`fixed inset-0 z-[60] overflow-hidden transition-opacity duration-300 md:hidden ${open ? "pointer-events-auto visible opacity-100" : "pointer-events-none invisible opacity-0"}`}
        data-testid="mobile-menu-overlay"
        aria-hidden={!open}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <aside
          data-testid="mobile-menu-sheet"
          className={`absolute left-0 top-0 flex h-full w-[85%] max-w-sm flex-col overflow-y-auto border-r-4 border-black bg-white shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between border-b-2 border-black px-6 py-5">
            <span className="flex items-center font-display text-lg font-black tracking-tighter text-black">
              VVS<span className="ml-1 bg-black px-1.5 py-0.5 text-white">LAB</span>
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close-btn"
              className="flex h-9 w-9 items-center justify-center border-2 border-black bg-white text-black transition-colors duration-200 hover:bg-black hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          <div className="border-b-2 border-black px-6 py-5">
            {signedIn ? (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Signed in as</p>
                <p className="mt-1 truncate text-sm font-bold text-black" data-testid="mobile-menu-user-name">{user.email}</p>
                <button
                  type="button"
                  onClick={() => { logout(); setOpen(false); }}
                  data-testid="mobile-menu-logout-btn"
                  className="mt-4 inline-flex items-center gap-2 border-2 border-black bg-black px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black"
                >
                  <LogOut size={12} /> Sign out
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Link
                  to="/login"
                  data-testid="mobile-menu-login-link"
                  className="flex flex-1 items-center justify-center gap-2 border-2 border-black bg-black px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black"
                >
                  <LogIn size={12} /> Sign In
                </Link>
                <Link
                  to="/signup"
                  data-testid="mobile-menu-signup-link"
                  className="flex flex-1 items-center justify-center gap-2 border-2 border-black bg-white px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-black hover:bg-black hover:text-white"
                >
                  <UserPlus size={12} /> Sign Up
                </Link>
              </div>
            )}
          </div>

          <nav className="flex-1 px-6 py-4">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => { setOpen(false); setSearchOpen(true); }}
                data-testid="mobile-menu-search-trigger"
                className="flex w-full items-center gap-3 border-2 border-black bg-white px-4 py-3 text-left text-sm text-zinc-500 transition-colors duration-200 hover:bg-black hover:text-white"
              >
                <Search size={16} />
                <span>Search chains, watches, grillz…</span>
              </button>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Shop</p>
            <div className="mt-3 space-y-1">
              {primaryLinks.map((l) => {
                const hasGroups = Array.isArray(l.groups) && l.groups.length > 0;
                if (!hasGroups) {
                  return (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between border-b border-black/10 py-3 text-sm font-bold uppercase tracking-widest transition-colors duration-200 ${isActive ? "text-[#1e40af]" : "text-black hover:text-[#1e40af]"}`
                      }
                    >
                      <span>{l.label}</span>
                      <span aria-hidden>→</span>
                    </NavLink>
                  );
                }
                return (
                  <details
                    key={l.to}
                    className="group border-b border-black/10 [&_summary::-webkit-details-marker]:hidden"
                    data-testid={`mobile-menu-group-${l.label.toLowerCase()}`}
                  >
                    <summary
                      className="flex cursor-pointer list-none items-center justify-between py-3 text-sm font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:text-[#1e40af]"
                    >
                      <span>{l.label}</span>
                      <span
                        aria-hidden
                        className="text-lg font-black leading-none text-black transition-transform duration-200 group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <div className="pb-3 pl-2">
                      <NavLink
                        to={l.to}
                        data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                        className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-zinc-500 underline-offset-4 hover:underline"
                      >
                        View all {l.label} →
                      </NavLink>
                      {l.groups.map((g, gi) => (
                        <div key={gi} className="mt-3 first:mt-0">
                          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400">{g.title}</p>
                          <ul className="mt-1 space-y-0.5">
                            {g.items.map((sub) => (
                              <li key={sub.to}>
                                <NavLink
                                  to={sub.to}
                                  data-testid={`mobile-menu-sublink-${sub.to.replace(/^\//, "")}`}
                                  className={({ isActive }) =>
                                    `block py-1.5 text-sm normal-case tracking-normal ${isActive ? "font-bold text-[#1e40af]" : "text-zinc-800 hover:text-[#1e40af]"}`
                                  }
                                >
                                  {sub.label}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
            </div>

            <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Account</p>
            <div className="mt-3 space-y-1">
              {utilityLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-menu-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-black/10 py-3 text-sm font-bold uppercase tracking-widest transition-colors duration-200 ${isActive ? "text-[#1e40af]" : "text-black hover:text-[#1e40af]"}`
                  }
                >
                  <span>{l.label}</span>
                  {l.label === "Wishlist" && wCount > 0 && (
                    <span className="ml-2 bg-[#1e40af] px-2 py-0.5 text-[10px] font-bold text-white">{wCount}</span>
                  )}
                  {l.label === "Cart" && count > 0 && (
                    <span className="ml-2 bg-black px-2 py-0.5 text-[10px] font-bold text-white">{count}</span>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          <div className="border-t-2 border-black px-6 py-4 text-[10px] uppercase tracking-widest text-zinc-500">
            © {new Date().getFullYear()} VVS Lab Jewelry
          </div>
        </aside>
      </div>
    </>
  );
};
